import React, { useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Link } from "react-router-dom"
import { CheckCircle2, Church, Send } from "lucide-react"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/ChurchOnboarding.css"

const normalizeMode = (value) => {
  const normalized = String(value || "").trim().toLowerCase()
  return normalized === "application" || normalized === "application required" || normalized === "by application"
    ? "Application"
    : "Open"
}

const groupName = (churchRecord) => churchRecord?.groupName || churchRecord?.congregationName || "this church"

export default function ChurchConnectCard({ churchRecord, compact = false, onConnected }) {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const [message, setMessage] = useState("")
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [status, setStatus] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const apiUrl = getApiBaseUrl()
  const membershipMode = normalizeMode(churchRecord?.membershipMode || churchRecord?.eligibleTo)
  const churchId = churchRecord?.groupId || churchRecord?.id

  const connect = async () => {
    setError("")

    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: `${window.location.pathname}${window.location.search}` },
      })
      return
    }

    if (!churchId) {
      setError("This church profile is missing its connection id.")
      return
    }

    setLoading(true)
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}/api/ChurchInvites/churches/${churchId}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          agreementAccepted,
        }),
      })

      if (!response.ok) {
        throw new Error(response.status === 409
          ? "This church connection could not be completed yet."
          : "Could not send this church connection request.")
      }

      const result = await response.json()
      setStatus(result.status)
      onConnected?.(result)
    } catch (err) {
      setError(err.message || "Could not connect to this church.")
    } finally {
      setLoading(false)
    }
  }

  if (status === "Joined" || status === "AlreadyMember") {
    return (
      <div className="church-connect-success">
        <CheckCircle2 size={18} aria-hidden="true" />
        <div>
          <strong>{status === "AlreadyMember" ? "Already connected" : "You are connected"}</strong>
          <Link to={`/group-members/${churchId}`}>Open church dashboard</Link>
        </div>
      </div>
    )
  }

  if (status === "ApplicationSubmitted") {
    return (
      <div className="church-connect-success">
        <CheckCircle2 size={18} aria-hidden="true" />
        <div>
          <strong>Request sent</strong>
          <span>Your request is waiting for this church's member review process.</span>
        </div>
      </div>
    )
  }

  return (
    <div className={compact ? "church-connect-card is-compact" : "church-connect-card"}>
      <div className="church-connect-heading">
        <Church size={18} aria-hidden="true" />
        <div>
          <strong>{membershipMode === "Application" ? "Request to connect" : "Connect with this church"}</strong>
          <span>
            {membershipMode === "Application"
              ? `${groupName(churchRecord)} asks visitors to request membership before joining.`
              : `${groupName(churchRecord)} is open for people to connect.`}
          </span>
        </div>
      </div>

      {membershipMode === "Application" && (
        <div className="church-connect-form">
          <label htmlFor={`church-message-${churchId}`}>Short note</label>
          <textarea
            id={`church-message-${churchId}`}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Share how you are connected to this church or why you want to connect."
            rows={compact ? 2 : 4}
          />
          <label className="church-connect-checkbox">
            <input
              type="checkbox"
              checked={agreementAccepted}
              onChange={(event) => setAgreementAccepted(event.target.checked)}
            />
            <span>I understand this church may have a membership covenant or conduct agreement.</span>
          </label>
        </div>
      )}

      {error && <div className="church-connect-error" role="alert">{error}</div>}

      <button type="button" className="church-connect-action" onClick={connect} disabled={loading}>
        <Send size={16} aria-hidden="true" />
        {loading
          ? "Sending..."
          : membershipMode === "Application"
          ? "Send request"
          : "Connect now"}
      </button>
    </div>
  )
}
